import { useScrollToTop } from '@app/core/hooks/useScrollToTop';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, IconButton, SxProps } from '@mui/material';
import { forwardRef, useImperativeHandle, useRef } from 'react';

/** Imperative handle exposed by ScrollableWithBackToTop via ref. */
export interface ScrollableWithBackToTopHandle {
  scrollToTop(): void;
  getIsAtTop(): boolean;
}

export interface ScrollableWithBackToTopProps {
  children: React.ReactNode;
  /** Called when the user scrolls to top (via the button or ref.scrollToTop()). */
  onScrollToTop?: () => void;
  /** Scroll position (px) below which the list is considered "at top". Default 5. */
  scrollTopThresholdPx?: number;
  /** Optional sx for the outer wrapper Box. */
  sx?: SxProps;
  /** Horizontal alignment of the back-to-top button. Default 'center'. */
  backToTopPosition?: 'center' | 'right';

  /** Whether to add padding bottom to the scroll container when the back-to-top button is visible. Default true. */
  addPaddingBottom?: boolean;
}

/**
 * Scrollable area that owns the scroll container ref and shows a back-to-top button when scrolled down.
 * Exposes scrollToTop() and getIsAtTop() via ref; callers never touch the DOM ref.
 */
export const ScrollableWithBackToTop = forwardRef<
  ScrollableWithBackToTopHandle | null,
  ScrollableWithBackToTopProps
>(function ScrollableWithBackToTop(
  {
    children,
    onScrollToTop,
    scrollTopThresholdPx,
    sx,
    backToTopPosition = 'center',
    addPaddingBottom = true,
  },
  ref,
) {
  const { scrollContainerRef, isAtTop, onScroll, scrollToTop, getIsAtTop } =
    useScrollToTop(scrollTopThresholdPx);

  const onScrollToTopRef = useRef(onScrollToTop);
  onScrollToTopRef.current = onScrollToTop;

  useImperativeHandle(
    ref,
    () => ({
      scrollToTop() {
        scrollToTop();
        onScrollToTopRef.current?.();
      },
      getIsAtTop,
    }),
    [scrollToTop, getIsAtTop],
  );

  const handleBackToTopClick = () => {
    scrollToTop();
    onScrollToTopRef.current?.();
  };

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Box
        ref={scrollContainerRef}
        onScroll={onScroll}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          // When back-to-top is visible, add bottom padding so the button does not cover content
          paddingBottom: isAtTop ? 0 : addPaddingBottom ? '60px' : undefined,
        }}
      >
        {children}
      </Box>
      <IconButton
        onClick={handleBackToTopClick}
        size="small"
        sx={{
          position: 'absolute',
          bottom: 16,
          ...(backToTopPosition === 'right'
            ? { right: 32 }
            : { left: '50%', transform: 'translateX(-50%)' }),
          opacity: isAtTop ? 0 : 1,
          pointerEvents: isAtTop ? 'none' : 'auto',
          transition: 'opacity 0.2s ease-in-out',
          backgroundColor: 'background.paper',
          borderRadius: '50%',
          boxShadow: 1,
          '&:hover': {
            backgroundColor: 'grey.50',
          },
          '& .MuiSvgIcon-root': {
            fontSize: 20,
          },
        }}
      >
        <KeyboardArrowUpIcon />
      </IconButton>
    </Box>
  );
});
